import { Link } from "react-router-dom";
import type { Post } from "@/mocks/posts";
import { categories } from "@/mocks/categories";

function formatViews(views: number) {
  if (views >= 10000) return `${(views / 10000).toFixed(1)} 万`;
  return String(views);
}

interface PostCardProps {
  post: Post;
  variant?: "default" | "compact";
}

export default function PostCard({ post, variant = "default" }: PostCardProps) {
  const category = categories.find((c) => c.id === post.categoryId);

  if (variant === "compact") {
    return (
      <Link
        to={`/post/${post.id}`}
        className="group flex cursor-pointer items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 hover:bg-background-100"
      >
        <div className="relative h-20 w-28 shrink-0 overflow-hidden rounded-md bg-background-200">
          <img
            src={post.cover}
            alt={`${post.title}相关配图`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-foreground-900 transition-colors group-hover:text-primary-700">
            {post.title}
          </h3>
          <div className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
            <span>{post.date}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(post.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/post/${post.id}`} className="flex h-full cursor-pointer flex-col">
        <div className="relative h-52 w-full overflow-hidden bg-background-200">
          <img
            src={post.cover}
            alt={`${post.title}配图`}
            className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
          {post.hot && (
            <span className="absolute left-3 top-3 flex items-center gap-1 rounded-md bg-primary-600 px-2 py-1 text-xs font-semibold text-primary-50">
              <i className="ri-fire-line"></i>
              热议中
            </span>
          )}
        </div>
        <div className="flex flex-1 flex-col p-4 md:p-5">
          <div className="mb-2.5 flex items-center gap-2 text-xs">
            <span className="rounded-md bg-secondary-100 px-2 py-0.5 font-medium text-secondary-800">
              {category?.name ?? "资讯"}
            </span>
            <span className="text-foreground-500">{post.date}</span>
          </div>
          <h3 className="font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
            {post.title}
          </h3>
          <p className="mt-2.5 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
            {post.summary}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-background-200 pt-3.5">
            {post.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-background-100 px-2.5 py-0.5 text-xs text-foreground-600"
              >
                #{tag}
              </span>
            ))}
            <span className="ml-auto flex items-center gap-1 text-xs text-foreground-500">
              <i className="ri-eye-line"></i>
              {formatViews(post.views)}
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}

export { formatViews };